import { useCallback, useEffect, useRef, useState } from "react";

import { getBusinessEntityDetail } from "@/api/entities";
import type { BusinessEntityDetailVO } from "@/api/entities/type";

/**
 * 拉取工商实体详情
 * @param entityId 工商实体 ID
 * @param open 弹窗是否打开
 */
export const useEntityDetail = (entityId?: string, open = false) => {
  const [entityDetail, setEntityDetail] = useState<BusinessEntityDetailVO | null>(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  /** 按 ID 请求详情 */
  const loadDetail = useCallback(async (id: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await getBusinessEntityDetail(id);
      if (requestId !== requestIdRef.current) return;
      setEntityDetail(res.data ?? null);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setEntityDetail(null);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!open || !entityId) return;
    void loadDetail(entityId);
  }, [entityId, loadDetail, open]);

  /** 关闭弹窗时清空，避免下次打开闪旧数据 */
  useEffect(() => {
    if (open) return;
    requestIdRef.current += 1;
    setEntityDetail(null);
    setLoading(false);
  }, [open]);

  return {
    entityDetail,
    loading,
    /** 外部写入详情（如同步成功后刷新展示） */
    applyDetail: setEntityDetail,
    reload: () => entityId && loadDetail(entityId),
  };
};
